import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@platform-frontend/util';
import { EMPTY, Observable, concat, defer, fromEvent, of, timer } from 'rxjs';
import { catchError, delay, distinctUntilChanged, endWith, filter, map, retry, share, startWith, switchMap, take, takeWhile } from 'rxjs/operators';
import { PollingConfiguration } from './model/polling-configuration';
import { PollingStatus } from './model/polling-status';

@Injectable({
  providedIn: 'root'
})
export class PollingService {
  constructor(@Inject(WINDOW) private window: Window) {}

  /**
   * Creates an observables that can be used for polling. It will subsequently
   * - call the initialization function,
   * - call the polling function until the returned observable emits true,
   * - call the completing function,
   * - wait until the current window is visible for a given amount of time, and
   * - call the proceeding function.
   *
   * @param config Configuration object to specify polling
   * parameters
   */
  getPollingObservable(config: PollingConfiguration): Observable<PollingStatus> {
    return this.buildPollingObservable({
      initialize: () => EMPTY,
      complete: () => EMPTY,
      proceed: () => of(null),
      initializeRetryCount: 3,
      completeRetryCount: 3,
      proceedRetryCount: 3,
      pollingInterval: 2200,
      waitingDuration: 1000,
      ...config
    });
  }

  private buildPollingObservable(config: Required<PollingConfiguration>): Observable<PollingStatus> {
    const initialize$ = defer(() => config.initialize()).pipe(
      retry(config.initializeRetryCount - 1),
      filter((v): v is never => false),
      startWith<PollingStatus>('polling')
    );

    const poll$ = timer(0, config.pollingInterval).pipe(
      switchMap(() =>
        config.poll(config.pollingInterval).pipe(
          map<boolean, PollingStatus>(pollingFinished => (pollingFinished ? 'done' : 'polling')),
          catchError(() => of<PollingStatus>('reconnecting'))
        )
      ),
      takeWhile(pollingStatus => pollingStatus !== 'done'),
      filter(pollingStatus => pollingStatus !== 'done')
    );

    const complete$ = defer(() => config.complete()).pipe(
      retry(config.completeRetryCount - 1),
      filter((v): v is never => false),
      endWith<PollingStatus>('done')
    );

    // emits once and completes when the current window becomes visible
    const pageVisible$ = fromEvent(this.window.document, 'visibilitychange').pipe(
      map(() => undefined),
      startWith(undefined),
      filter(() => !this.window.document.hidden),
      take(1)
    );

    const wait$ = pageVisible$.pipe(
      delay(config.waitingDuration),
      filter((v): v is never => false)
    );

    const proceed$ = defer(() => config.proceed()).pipe(
      retry(config.proceedRetryCount - 1),
      filter((v): v is never => false)
    );

    return concat(initialize$, poll$, complete$, wait$, proceed$).pipe(share(), distinctUntilChanged());
  }
}
